import { useState } from 'react'
import { z } from 'zod'
import {
  fiscalYearOf,
  getFiscalYearLabel,
  manualTransactionSourceEnum,
  selectableFiscalYears,
  type BudgetCode,
  type NewTransaction,
} from '@budgetmonkey/shared'
import { Button, ErrorNote, Field, Input, Select, Textarea } from '@/components/ui'
import { BudgetCodeField } from '@/features/budgetCodes/BudgetCodePicker'
import { dateInputToIso, isoToDateInput, todayInput } from '@/lib/dates'

/** The sources a human can pick — `csiu` belongs to the importer alone (§6.5). */
type ManualSource = z.infer<typeof manualTransactionSourceEnum>

/**
 * Manual transaction entry — REQUIREMENTS.md §6.5.
 *
 * The lowest-priority capture channel (mileage, cash refunds, anything with no
 * receipt), but the only one that exists in Phase 1, so it's also how the
 * ledger gets exercised before receipt capture arrives in Phase 2.
 */

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual entry',
  pcard: 'P-Card',
  amazon: 'Amazon',
  po: 'Purchase order',
  reimbursement: 'Reimbursement',
}

const SOURCE_HINTS: Record<string, string> = {
  pcard: 'Appears on your paper statement; confirmed when you submit the packet.',
  amazon: 'Confirmed when a CSIU import matches it.',
  po: 'Confirmed when a CSIU import matches it.',
  reimbursement: 'Confirmed when a CSIU import matches it.',
  manual: 'No receipt expected — mileage, cash, and the like.',
}

export interface TransactionFormValues extends NewTransaction {
  /**
   * Only ever set when editing. A new charge derives its year from its
   * purchase date and has nothing to choose — see `useCreateTransaction`.
   */
  fiscalYear?: number
}

/**
 * Values a receipt can pre-fill (§7.1 step 6). Anything the extraction refused
 * is simply absent, which leaves that field empty rather than guessed at.
 */
export interface TransactionFormPrefill {
  vendor?: string | null | undefined
  amount?: number | null | undefined
  /** ISO timestamp; only the calendar day is used. */
  transactionDate?: string | null | undefined
  source?: ManualSource | undefined
  /**
   * These three carry no receipt extraction — nothing reads them off a photo.
   * They exist so an *edit* can start from what is already stored. Without
   * them the form opens blank in those fields and saving silently wipes a
   * description someone typed, which is worse than not offering an edit.
   */
  description?: string | undefined
  notes?: string | undefined
  /** ISO timestamp. Only set when it differs from the purchase date. */
  dateReceived?: string | null | undefined
  /** The stored budget year. Edit only; absent means "derive from the date". */
  fiscalYear?: number | undefined
}

export function TransactionForm({
  initialCode,
  prefill,
  submitLabel,
  onSubmit,
  onCancel,
  pending,
  error,
  fiscalYear,
  children,
}: {
  initialCode?: BudgetCode | null | undefined
  prefill?: TransactionFormPrefill | undefined
  submitLabel: string
  onSubmit: (values: TransactionFormValues) => void
  onCancel: () => void
  pending: boolean
  error?: string | undefined
  fiscalYear?: number | undefined
  /** Slotted above the fields — the receipt review uses it for its warnings. */
  children?: React.ReactNode
}) {
  const [code, setCode] = useState<BudgetCode | null>(initialCode ?? null)
  const [transactionDate, setTransactionDate] = useState(
    prefill?.transactionDate != null
      ? isoToDateInput(prefill.transactionDate)
      : todayInput(),
  )
  // `undefined` means "follow the purchase date". Only once the user edits it
  // does this become an independent value — see the note below. An edit starts
  // it independent only when the stored value genuinely differs, so a saved
  // transaction reopens showing the same "same as purchase" it showed before.
  const [dateReceived, setDateReceived] = useState<string | undefined>(() => {
    if (prefill?.dateReceived == null || prefill.transactionDate == null) return undefined
    const received = isoToDateInput(prefill.dateReceived)
    return received === isoToDateInput(prefill.transactionDate) ? undefined : received
  })
  const [amount, setAmount] = useState(
    prefill?.amount != null ? prefill.amount.toFixed(2) : '',
  )
  const [vendor, setVendor] = useState(prefill?.vendor ?? '')
  const [description, setDescription] = useState(prefill?.description ?? '')
  const [source, setSource] = useState<ManualSource>(prefill?.source ?? 'manual')
  const [notes, setNotes] = useState(prefill?.notes ?? '')
  const [touched, setTouched] = useState(false)
  // Independent of the purchase date once the form is open. The date usually
  // decides it, but the one case this control exists for is the case where it
  // shouldn't: a June charge on a July statement that the business office has
  // already booked to the new year.
  const [budgetYear, setBudgetYear] = useState<number | undefined>(prefill?.fiscalYear)

  // §8.1's "Date Item Received" column. Matt confirmed this equals the purchase
  // date almost always — meals, travel, services — and differs only for
  // physical goods that ship late. So it tracks the purchase date until
  // deliberately changed, rather than being a second date to fill in.
  const effectiveDateReceived = dateReceived ?? transactionDate

  const amountNumber = Number(amount)
  const amountProblem =
    touched && (amount.trim() === '' || Number.isNaN(amountNumber) || amountNumber === 0)
      ? 'Enter an amount. Use a negative number for a refund or credit.'
      : null
  const vendorProblem = touched && vendor.trim() === '' ? 'Required.' : null
  const codeProblem = touched && code === null ? 'Choose a budget code.' : null

  const canSubmit =
    code !== null &&
    vendor.trim() !== '' &&
    amount.trim() !== '' &&
    !Number.isNaN(amountNumber) &&
    amountNumber !== 0 &&
    dateInputToIso(transactionDate) !== null

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setTouched(true)
    if (!canSubmit || code === null) return

    const purchaseIso = dateInputToIso(transactionDate)
    const receivedIso = dateInputToIso(effectiveDateReceived)
    if (purchaseIso === null) return

    onSubmit({
      budgetCodeId: code.id ?? '',
      transactionDate: purchaseIso,
      dateReceived: receivedIso ?? purchaseIso,
      amount: amountNumber,
      vendor: vendor.trim(),
      description: description.trim(),
      source,
      notes: notes.trim(),
      ...(budgetYear !== undefined ? { fiscalYear: budgetYear } : {}),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {error !== undefined && <ErrorNote>{error}</ErrorNote>}

      {children}

      <BudgetCodeField
        value={code}
        onChange={setCode}
        fiscalYear={fiscalYear}
        error={codeProblem ?? undefined}
      />

      {code?.external === true && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This is {code.externalOwnerName}'s code. The charge will appear on your
          reconciliation packet but won't count against any of your balances.
        </p>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Vendor" required error={vendorProblem ?? undefined}>
          <Input
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Conwell Inn"
            invalid={vendorProblem !== null}
            autoComplete="off"
          />
        </Field>

        <Field
          label="Amount"
          required
          error={amountProblem ?? undefined}
          hint="Negative for a refund or credit."
        >
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="0.00"
            inputMode="decimal"
            invalid={amountProblem !== null}
          />
        </Field>

        <Field label="Date of purchase" required>
          <Input
            type="date"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
          />
        </Field>

        <Field label="Source" hint={SOURCE_HINTS[source]}>
          <Select
            value={source}
            onChange={(e) => setSource(e.target.value as ManualSource)}
          >
            {manualTransactionSourceEnum.options.map((option) => (
              <option key={option} value={option}>
                {SOURCE_LABELS[option] ?? option}
              </option>
            ))}
          </Select>
        </Field>

        {/* Edit only. A new charge takes its year from the purchase date and
            has nothing to choose; this exists for the one cycle a year that
            straddles the rollover, where a June charge arrives on a July
            statement and which budget it came out of is a decision. */}
        {budgetYear !== undefined && (
          <Field
            label="Budget year"
            hint={
              fiscalYearOf(new Date(transactionDate)) === budgetYear
                ? 'Matches the purchase date.'
                : `Purchased in ${getFiscalYearLabel(
                    fiscalYearOf(new Date(transactionDate)),
                  )} — counted against ${getFiscalYearLabel(budgetYear)}.`
            }
          >
            <Select
              value={budgetYear}
              onChange={(e) => setBudgetYear(Number(e.target.value))}
            >
              {selectableFiscalYears().map((year) => (
                <option key={year} value={year}>
                  {getFiscalYearLabel(year)}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>

      <div className="space-y-3">
        <label className="flex items-center gap-2.5 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={dateReceived !== undefined}
            onChange={(e) =>
              setDateReceived(e.target.checked ? transactionDate : undefined)
            }
            className="size-4 rounded border-slate-300"
          />
          Item arrived on a different day
        </label>

        {dateReceived !== undefined && (
          <Field
            label="Date item received"
            hint="Prints in the log sheet's Date Item Received column."
          >
            <Input
              type="date"
              value={dateReceived}
              min={transactionDate}
              onChange={(e) => setDateReceived(e.target.value)}
            />
          </Field>
        )}
      </div>

      <Field label="Description" hint="Brief — this prints on the packet.">
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Hotel for Cyber Conference"
          maxLength={200}
        />
      </Field>

      <Field label="Notes" hint="For you. Not printed.">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      <div className="flex gap-3 pt-1">
        <Button type="submit" variant="primary" disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
